import React, { useEffect, useMemo, useRef, useState } from "react";
import APAdvancedSection from "@/pages/finance/APAdvancedSection";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { StatusBadge, EmptyState } from "@/components/app/ui-bits";
import { CheckCircle, PaperPlaneTilt, Receipt, GitMerge, WarningCircle, XCircle, Plus, Paperclip, FileText, CaretDown, MagnifyingGlass, X, BookOpenText } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { toast } from "sonner";
import { ARPaymentDialog } from "./ARTab";
import { useUpload } from "./_shared";
import { GLAccountSelect, GLPreview, GLEntriesDialog, usePostingMap } from "./GLAccountBits";
import BranchFilter from "./BranchFilter";
import RecordInspector, { useRecordInspector } from "@/components/app/RecordInspector";

const CHECK_ICON = {
  pass: <CheckCircle size={14} weight="fill" className="text-emerald-600" />,
  warn: <WarningCircle size={14} weight="fill" className="text-amber-500" />,
  fail: <XCircle size={14} weight="fill" className="text-rose-600" />,
};

function ExceptionForm({ billId, onDone }) {
  const [reason, setReason] = useState("");
  const approve = async () => {
    try {
      await api.post(`/finance/ap/${billId}/match-exception`, { reason });
      toast.success("Match exception approved — payment unblocked");
      setReason(""); onDone();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="flex gap-2 pt-1" data-testid="twm-exception-form">
      <Input className="h-8 flex-1" placeholder="Exception reason (documented override)…" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="twm-exception-reason" />
      <Button size="sm" variant="outline" className="h-8" onClick={approve} disabled={!reason.trim()} data-testid="twm-exception-approve">Approve exception</Button>
    </div>
  );
}

function MatchDialog({ bill, canEdit, onClose, onChanged }) {
  const [match, setMatch] = useState(null);
  const [pos, setPos] = useState([]);
  const [selectedPo, setSelectedPo] = useState("");

  const load = async () => {
    if (!bill) return;
    try {
      const [{ data: m }, procRes] = await Promise.all([
        api.get(`/finance/ap/${bill.id}/three-way-match`),
        api.get("/procurement").catch(() => ({ data: { items: [] } })),
      ]);
      setMatch(m);
      setPos(procRes.data?.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { setMatch(null); setSelectedPo(""); load(); }, [bill]); // eslint-disable-line react-hooks/exhaustive-deps

  const linkPo = async () => {
    if (!selectedPo) return;
    try {
      const { data } = await api.post(`/finance/ap/${bill.id}/link-po`, { procurement_id: selectedPo });
      setMatch(data);
      toast.success("PO linked — match recomputed");
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={!!bill} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        {!bill ? null : (<>
        <DialogHeader>
          <DialogTitle>3-way match · {bill?.bill_number}</DialogTitle>
          <DialogDescription>PO ↔ goods receipt ↔ bill. Payments are blocked while the match fails.</DialogDescription>
        </DialogHeader>
        {!match ? (
          <div className="font-mono-alt text-xs uppercase tracking-widest text-slate-400">Checking…</div>
        ) : (
          <div className="space-y-2" data-testid="threeway-match-detail">
            {match.po && (
              <div className="rounded-md bg-slate-50 border border-slate-200 p-2.5 text-xs">
                <b>{match.po.ref}</b> · {match.po.title} · {match.po.counterparty || "—"} · {match.po.value ? formatCurrency(match.po.value) : "no value"}
              </div>
            )}
            {match.checks.map((c) => (
              <div key={c.id} className="flex items-start gap-2 rounded-md border border-slate-200 p-2.5" data-testid={`twm-check-${c.id}`}>
                <div className="mt-0.5">{CHECK_ICON[c.status]}</div>
                <div>
                  <div className="text-xs font-semibold text-slate-800">{c.label}</div>
                  <div className="text-[11px] text-slate-500">{c.detail}</div>
                </div>
              </div>
            ))}
            <div className={`rounded-md px-3 py-2 text-xs font-semibold ${match.status === "matched" ? "bg-emerald-50 text-emerald-700" : match.status === "matched_with_warnings" ? "bg-amber-50 text-amber-700" : match.status === "exception_approved" ? "bg-indigo-50 text-indigo-700" : "bg-rose-50 text-rose-700"}`} data-testid="twm-verdict">
              {match.status === "matched" ? "MATCHED ✓ — payment allowed" :
                match.status === "matched_with_warnings" ? "MATCHED WITH WARNINGS — payment allowed" :
                match.status === "exception_approved" ? "EXCEPTION APPROVED — payment allowed" :
                "MISMATCH — payment blocked"}
            </div>
            {match.exception && (
              <div className="rounded-md bg-indigo-50 border border-indigo-200 px-3 py-2 text-[11px] text-indigo-800" data-testid="twm-exception-info">
                Exception by {match.exception.approved_by}: {match.exception.reason}
              </div>
            )}
            {canEdit && match.status === "mismatch" && (
              <ExceptionForm billId={bill.id} onDone={() => { load(); onChanged(); }} />
            )}
          </div>
        )}
        {canEdit && (
          <div className="space-y-1 border-t border-slate-100 pt-3">
            <Label className="text-xs">Link a procurement record (PO)</Label>
            <div className="flex gap-2">
              <Select value={selectedPo || undefined} onValueChange={setSelectedPo}>
                <SelectTrigger className="h-8 flex-1" data-testid="twm-po-select"><SelectValue placeholder="Choose PO…" /></SelectTrigger>
                <SelectContent>
                  {pos.slice(0, 100).map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.ref} · {(p.title || "").slice(0, 40)} · {p.estimated_value ? formatCurrency(p.estimated_value) : "—"}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button size="sm" className="h-8" onClick={linkPo} disabled={!selectedPo} data-testid="twm-link-btn">Link</Button>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
        </>)}
      </DialogContent>
    </Dialog>
  );
}

export default function APTab({ canEdit, projects = [] }) {
  const inspector = useRecordInspector();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payOpen, setPayOpen] = useState(null);
  const [matchBill, setMatchBill] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [glView, setGlView] = useState(null);
  const [branch, setBranch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get("/finance/ap", { params: branch ? { branch_id: branch } : {} }),
        api.get("/finance/ap/summary"),
      ]);
      setRows(r.data);
      setSummary(s.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [branch]); // eslint-disable-line react-hooks/exhaustive-deps

  const requestApproval = async (id) => {
    try {
      await api.post(`/finance/ap/${id}/request-approval`);
      toast.success("Approval requested · routed to CEO");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div className="dc-card p-4 border-l-4 border-l-[#1E5CBA]">
          <div className="overline">Total Due</div>
          <div className="mt-1 font-display text-2xl font-bold tabular-nums text-slate-900">{summary ? formatCurrency(summary.total_due) : "—"}</div>
          <div className="text-xs text-slate-500">{summary?.count || 0} bill(s)</div>
        </div>
        <div className="dc-card p-4 border-l-4 border-l-emerald-500">
          <div className="overline">Due in 0–30d</div>
          <div className="mt-1 font-display text-2xl font-bold tabular-nums text-emerald-700">{summary ? formatCurrency(summary.current) : "—"}</div>
        </div>
        <div className="dc-card p-4 border-l-4 border-l-amber-500">
          <div className="overline">Due 31–60d</div>
          <div className="mt-1 font-display text-2xl font-bold tabular-nums text-amber-700">{summary ? formatCurrency(summary["31_60"]) : "—"}</div>
        </div>
        <div className="dc-card p-4 border-l-4 border-l-red-500">
          <div className="overline">Overdue 60d+</div>
          <div className="mt-1 font-display text-2xl font-bold tabular-nums text-red-700">{summary ? formatCurrency(summary["60_plus"]) : "—"}</div>
        </div>
      </div>

      <div className="dc-card overflow-hidden">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <div>
            <h3 className="font-display text-[15px] font-semibold text-slate-900">Vendor payables</h3>
            <div className="text-[11px] text-slate-400">Bills linked to a PO are payment-gated by the 3-way match (PO ↔ goods receipt ↔ bill).</div>
          </div>
          <div className="flex items-center gap-2">
            <BranchFilter value={branch} onChange={setBranch} />
            {canEdit && (
              <Button onClick={() => setAddOpen(true)} className="btn-primary-blue" data-testid="ap-add-btn">
                <Plus size={14} className="mr-1" /> New bill
              </Button>
            )}
          </div>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
        ) : rows.length === 0 ? (
          <EmptyState icon={Receipt} title="No payables" description="No outstanding bills tracked yet." />
        ) : (
          <div className="responsive-table-wrap">
            <table className="data-table w-full min-w-[1100px]">
              <thead>
                <tr>
                  <th>Vendor</th><th>Bill #</th><th>Date</th><th>Due</th>
                  <th className="text-right">Total</th><th className="text-right">Balance</th>
                  <th>PO</th><th>Status</th><th className="w-52">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    className="text-sm cursor-pointer hover:bg-slate-50/70 transition-colors"
                    onClick={() => inspector.open(r, {
                      title: `Bill ${r.bill_number}`,
                      subtitle: `${r.vendor_name} · ${r.category || ""} · ${formatCurrency(r.total)}`,
                    })}
                    data-testid={`ap-row-${r.bill_number}`}
                  >
                    <td className="px-5 py-3 font-semibold text-slate-900">
                      {r.vendor_name}
                      <div className="text-xs text-slate-500">{r.category}</div>
                    </td>
                    <td className="px-5 py-3 font-mono-alt text-xs">{r.bill_number}</td>
                    <td className="px-5 py-3 font-mono-alt text-xs tabular-nums">{formatDate(r.bill_date)}</td>
                    <td className="px-5 py-3 font-mono-alt text-xs tabular-nums">{formatDate(r.due_date)}</td>
                    <td className="px-5 py-3 text-right font-mono-alt tabular-nums">{formatCurrency(r.total)}</td>
                    <td className="px-5 py-3 text-right font-mono-alt tabular-nums font-semibold">{formatCurrency(r.balance)}</td>
                    <td className="px-5 py-3">
                      {r.procurement_id ? (
                        <span className="rounded-full bg-blue-50 px-1.5 py-0.5 font-mono-alt text-[9px] font-semibold uppercase tracking-wider text-blue-700">{r.procurement_ref || "PO ✓"}</span>
                      ) : (
                        <span className="text-[10px] text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <StatusBadge status={
                        r.status === "current" ? "active"
                        : r.status === "due_soon" ? "expiring_soon"
                        : r.status === "overdue" ? "expired"
                        : r.status === "pending_approval" ? "pending"
                        : r.status === "approved_for_payment" ? "approved"
                        : r.status
                      } />
                    </td>
                    <td className="px-5 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm" variant="outline" className="h-8"
                          title="3-way match"
                          onClick={() => setMatchBill(r)}
                          data-testid={`ap-match-${r.id}`}
                        >
                          <GitMerge size={12} />
                        </Button>
                        <Button
                          size="sm" variant="outline" className="h-8"
                          title="View GL journal entries"
                          onClick={() => setGlView({ collection: "ap_bills", id: r.id, label: r.bill_number || r.vendor_name })}
                          data-testid={`ap-gl-${r.id}`}
                        >
                          <BookOpenText size={12} />
                        </Button>
                        {canEdit && r.status !== "paid" && (
                          <>
                            {!["pending_approval", "approved_for_payment"].includes(r.status) && (
                              <Button
                                size="sm" variant="outline" className="h-8"
                                title="Request CEO approval"
                                onClick={() => requestApproval(r.id)}
                                data-testid={`ap-request-${r.id}`}
                              >
                                <PaperPlaneTilt size={12} />
                              </Button>
                            )}
                            <Button
                              size="sm" variant="outline" className="h-8"
                              onClick={() => setPayOpen(r)}
                              data-testid={`ap-pay-${r.id}`}
                            >
                              <CheckCircle size={12} className="mr-1" />Pay
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Reuse AR payment dialog UX with bill-shaped record */}
      <ARPaymentDialog
        item={payOpen ? { ...payOpen, invoice_number: payOpen.bill_number, client_name: payOpen.vendor_name } : null}
        endpoint={payOpen ? `/finance/ap/${payOpen.id}/pay` : undefined}
        mode="ap"
        onClose={() => setPayOpen(null)}
        onDone={load}
      />
      <APAdvancedSection isAdmin={canEdit} />

      <MatchDialog bill={matchBill} canEdit={canEdit} onClose={() => setMatchBill(null)} onChanged={load} />
      <APAddDialog open={addOpen} onClose={() => setAddOpen(false)} projects={projects} onDone={load} />
      <GLEntriesDialog item={glView} onClose={() => setGlView(null)} />
      <RecordInspector {...inspector.props} />
    </div>
  );
}


// ──────────────────────────────────────────────────────────────
// New-Bill dialog (creates an ap_bill via POST /api/finance/ap)
const AP_CATEGORIES = [
  "Direct Labor", "Materials", "Subcontractors", "Equipment",
  "Travel & Logistics", "Permits & Fees", "Testing & QA",
  "Site Expenses", "Consumables",
  "Salaries & Benefits", "Rent & Utilities", "Insurance", "Office Supplies",
  "Software & Subscriptions", "Professional Fees", "Travel", "Marketing",
  "Bank Charges", "Miscellaneous",
];

function APAddDialog({ open, onClose, projects, onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  const due30 = new Date(Date.now() + 30 * 86400e3).toISOString().slice(0, 10);
  const blank = {
    vendor_id: "", vendor_name: "", project_id: "none", project_name: "",
    bill_number: "", bill_date: today, due_date: due30,
    total: 0, category: "Miscellaneous", notes: "",
    currency: "USD", fx_rate: "", tax_code: "none", expense_account: "",
    document_url: "", document_name: "", procurement_id: "",
  };
  const [form, setForm] = useState(blank);
  const [taxCodes, setTaxCodes] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [vendorSuggestions, setVendorSuggestions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [docError, setDocError] = useState("");
  const [dupWarn, setDupWarn] = useState(null);
  const [poOptions, setPoOptions] = useState([]);
  const fileRef = useRef(null);
  const { upload, busy: uploading } = useUpload();

  useEffect(() => {
    if (open) {
      setForm(blank);
      setDocError("");
      setDupWarn(null);
      api.get("/procurement").then(({ data }) => {
        const rows = (data.items || data || []).filter(
          (r) => ["B", "C"].includes(r.stream) && ["AWARDED", "ACTIVE"].includes(r.status) && (r.estimated_value || 0) > 0);
        setPoOptions(rows);
      }).catch(() => setPoOptions([]));
      api.get("/finance/tax/codes").then(({ data }) => setTaxCodes(data.items || [])).catch(() => {});
      api.get("/finance/fx/currencies").then(({ data }) => setCurrencies(data.items || [])).catch(() => {});
      // Vendor suggestions = active vendors register + AP outstanding balance per vendor_name
      Promise.all([
        api.get("/vendors", { params: { status: "active" } }).catch(() => ({ data: { items: [] } })),
        api.get("/finance/ap").catch(() => ({ data: [] })),
      ]).then(([venRes, apRes]) => {
        const outByName = {};
        (apRes.data || []).forEach((b) => {
          const key = (b.vendor_name || "").trim().toLowerCase();
          if (!key) return;
          if (!outByName[key]) outByName[key] = { outstanding: 0, count: 0, vendor_name: b.vendor_name };
          if (b.status !== "paid") outByName[key].outstanding += (b.balance || 0);
          outByName[key].count += 1;
        });
        const registerItems = (venRes.data?.items || []).map((v) => ({
          id: v.id, name: v.name, vendor_no: v.vendor_no, category: v.category,
          preferred: v.preferred,
          outstanding: outByName[v.name.trim().toLowerCase()]?.outstanding || 0,
          bill_count: outByName[v.name.trim().toLowerCase()]?.count || 0,
          from: "register",
        }));
        const regNames = new Set(registerItems.map((r) => r.name.trim().toLowerCase()));
        const orphanApVendors = Object.values(outByName)
          .filter((o) => !regNames.has(o.vendor_name.trim().toLowerCase()))
          .map((o) => ({
            id: "", name: o.vendor_name, vendor_no: "", category: "other",
            preferred: false, outstanding: o.outstanding, bill_count: o.count,
            from: "ap_only",
          }));
        const merged = [...registerItems, ...orphanApVendors].sort((a, b) => {
          if (b.preferred !== a.preferred) return b.preferred ? 1 : -1;
          return (b.outstanding || 0) - (a.outstanding || 0);
        });
        setVendorSuggestions(merged);
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const setCurrency = async (cur) => {
    setForm((f) => ({ ...f, currency: cur, fx_rate: "" }));
    if (cur !== "USD") {
      try {
        const { data } = await api.get("/finance/fx/rates/resolve", { params: { currency: cur } });
        setForm((f) => ({ ...f, currency: cur, fx_rate: data.rate }));
      } catch { /* rate blank — backend will reject with clear error */ }
    }
  };

  const taxRate = form.tax_code !== "none" ? (taxCodes.find((t) => t.code === form.tax_code)?.rate_pct || 0) : 0;
  const taxAmt = (form.total || 0) * taxRate / 100;
  const isFc = form.currency !== "USD";
  const curOptions = [...new Set(["EUR", "GBP", "KES", "ETB", "SOS", "AED", ...currencies.map((c) => c.currency)])];

  // Expense account — auto-suggested from the category (backend mapping), user can override.
  const pmap = usePostingMap();
  const hasProject = form.project_id && form.project_id !== "none";
  const defaultExp = pmap
    ? (pmap.project_cost?.[form.category] || pmap.overhead?.[form.category] || (hasProject ? "5199" : "6199"))
    : "";
  const effExp = form.expense_account || defaultExp;
  const glRows = (form.total || 0) > 0 && effExp
    ? [
        { code: effExp, debit: form.total },
        ...(taxAmt > 0 ? [{ code: "2131", debit: taxAmt }] : []),
        { code: "2110", credit: (form.total || 0) + taxAmt },
      ]
    : [];

  const onAttach = async (file) => {
    if (!file) return;
    setDocError("");
    try {
      const r = await upload(file);
      setForm((f) => ({ ...f, document_url: r.url, document_name: r.name }));
      toast.success(`Attached ${r.name}`);
    } catch (e) {
      setDocError(formatApiError(e));
      toast.error(formatApiError(e));
    }
  };

  const submit = async (force = false) => {
    if (!form.vendor_name.trim()) return toast.error("Vendor name is required");
    if (!form.bill_date) return toast.error("Bill date is required");
    if (!form.due_date) return toast.error("Due date is required");
    if ((form.total || 0) <= 0) return toast.error("Total must be greater than 0");
    setBusy(true);
    try {
      const proj = projects.find((p) => p.id === form.project_id);
      const body = {
        vendor_id: form.vendor_id || "",
        vendor_name: form.vendor_name.trim(),
        project_id: form.project_id === "none" ? "" : form.project_id,
        project_name: proj?.name || form.project_name || "",
        procurement_id: form.procurement_id || "",
        bill_number: form.bill_number.trim(),
        bill_date: form.bill_date,
        due_date: form.due_date,
        notes: form.notes,
        category: form.category,
        expense_account: effExp,
        currency: form.currency,
        tax_code: form.tax_code === "none" ? "" : form.tax_code,
        document_url: form.document_url || "",
        document_name: form.document_name || "",
        force,
      };
      if (isFc) {
        body.fc_total = form.total;
        body.total = 0;
        if (form.fx_rate) body.fx_rate = parseFloat(form.fx_rate);
      } else {
        body.total = form.total;
      }
      await api.post("/finance/ap", body);
      setDupWarn(null);
      toast.success(force ? "Bill created (duplicate override recorded)" : "Bill created");
      onClose();
      onDone();
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (e?.response?.status === 409 && det?.code === "duplicate_suspected") {
        setDupWarn(det);
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="ap-add-dialog">
        <DialogHeader>
          <DialogTitle>New vendor bill</DialogTitle>
          <DialogDescription>Record a payable — routes through 3-way match &amp; CEO approval before payment.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {poOptions.length > 0 && (
            <div className="col-span-2">
              <Label>Create from purchase order (optional)</Label>
              <Select value={form.procurement_id || "none"} onValueChange={(v) => {
                if (v === "none") { setForm((f) => ({ ...f, procurement_id: "" })); return; }
                const po = poOptions.find((r) => r.id === v);
                if (!po) return;
                setForm((f) => ({
                  ...f, procurement_id: po.id,
                  vendor_name: po.counterparty || f.vendor_name,
                  vendor_id: po.vendor_id || "",
                  project_id: po.project_id || f.project_id,
                  total: f.total || po.estimated_value || 0,
                  notes: f.notes || `PO ${po.ref} · ${po.title}`,
                }));
              }}>
                <SelectTrigger data-testid="ap-add-po"><SelectValue placeholder="No PO — free-form bill" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No PO — free-form bill</SelectItem>
                  {poOptions.map((r) => (
                    <SelectItem key={r.id} value={r.id}>{r.ref} · {r.title} · {r.counterparty || "?"} (${(r.estimated_value || 0).toLocaleString()})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.procurement_id && <div className="mt-1 text-[10px] text-blue-700">Linked to PO — the bill will be validated by 3-way match before payment.</div>}
            </div>
          )}
          <div className="col-span-2">
            <Label>Vendor *</Label>
            <VendorAutocomplete
              value={form.vendor_name}
              vendorId={form.vendor_id}
              suggestions={vendorSuggestions}
              onSelect={(v) => setForm((f) => ({ ...f, vendor_name: v.name, vendor_id: v.id || "" }))}
              onFreeText={(txt) => setForm((f) => ({ ...f, vendor_name: txt, vendor_id: "" }))}
            />
          </div>
          <div className="col-span-2">
            <Label>Project</Label>
            <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
              <SelectTrigger data-testid="ap-add-project"><SelectValue placeholder="Optional — link to a project" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No project (overhead)</SelectItem>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}{p.code ? ` · ${p.code}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Bill number</Label>
            <Input value={form.bill_number} onChange={(e) => setForm({ ...form, bill_number: e.target.value })} placeholder="e.g. ABC-INV-0001" data-testid="ap-add-bill-number" />
          </div>
          <div>
            <Label>Category *</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v, expense_account: "" })}>
              <SelectTrigger data-testid="ap-add-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {AP_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Bill date *</Label>
            <Input type="date" value={form.bill_date} onChange={(e) => setForm({ ...form, bill_date: e.target.value })} data-testid="ap-add-bill-date" />
          </div>
          <div>
            <Label>Due date *</Label>
            <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="ap-add-due-date" />
          </div>
          <div>
            <Label>Currency</Label>
            <Select value={form.currency} onValueChange={setCurrency}>
              <SelectTrigger data-testid="ap-add-currency"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="USD">USD (base)</SelectItem>
                {curOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {isFc && (
            <div>
              <Label>FX rate ({form.currency}/USD)</Label>
              <Input type="number" step="0.000001" value={form.fx_rate} onChange={(e) => setForm({ ...form, fx_rate: e.target.value })} data-testid="ap-add-fx-rate" />
            </div>
          )}
          <div className={isFc ? "col-span-2" : ""}>
            <Label>Subtotal ({form.currency}) *</Label>
            <Input type="number" step="0.01" value={form.total} onChange={(e) => setForm({ ...form, total: parseFloat(e.target.value || 0) })} data-testid="ap-add-total" />
          </div>
          <div className={isFc ? "col-span-2" : ""}>
            <Label>Tax</Label>
            <Select value={form.tax_code} onValueChange={(v) => setForm({ ...form, tax_code: v })}>
              <SelectTrigger data-testid="ap-add-tax-code"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No tax</SelectItem>
                {taxCodes.filter((t) => t.kind === "input").map((t) => <SelectItem key={t.code} value={t.code}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {taxRate > 0 && (
              <div className="mt-1 text-[10px] text-slate-500" data-testid="ap-add-tax-hint">
                + {taxRate}% tax {taxAmt.toFixed(2)} {form.currency} → gross {((form.total || 0) + taxAmt).toFixed(2)} {form.currency}
              </div>
            )}
          </div>
          <div className="col-span-2">
            <Label>Expense account (debited)</Label>
            <GLAccountSelect value={effExp} onChange={(v) => setForm({ ...form, expense_account: v })} types={["expense", "asset"]} testid="ap-add-expense-account" />
            <div className="mt-1 text-[10px] text-slate-500">Auto-suggested from the category — the payable (2110) is credited automatically.</div>
          </div>
          <div className="col-span-2">
            <GLPreview
              rows={glRows}
              currency={form.currency}
              note={isFc ? "Posted to the GL in USD at the booking FX rate." : undefined}
              testid="ap-add-gl-preview"
            />
          </div>
          {/* Attachment — scanned invoice / receipt */}
          <div className="col-span-2 rounded-md border border-dashed border-slate-300 bg-slate-50 p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <Label className="flex items-center gap-1.5">
                  <Paperclip size={12} /> Scanned invoice / receipt
                </Label>
                <div className="mt-0.5 text-[11px] text-slate-500">
                  PDF or image (JPG/PNG). Attach the vendor invoice so the 3-way match evidence lives with the bill (max 15 MB).
                </div>
                {form.document_name && (
                  <div className="mt-1.5 inline-flex items-center gap-1.5 rounded-md bg-white border border-slate-200 px-2 py-1 text-[11px] text-slate-700" data-testid="ap-add-doc-name">
                    <FileText size={11} className="text-emerald-600" weight="fill" />
                    <a href={form.document_url} target="_blank" rel="noreferrer" className="truncate max-w-[220px] hover:underline">{form.document_name}</a>
                    <button
                      type="button"
                      className="ml-1 text-slate-400 hover:text-red-600"
                      onClick={() => setForm((f) => ({ ...f, document_url: "", document_name: "" }))}
                      data-testid="ap-add-doc-clear"
                      title="Remove attachment"
                    >
                      <X size={10} />
                    </button>
                  </div>
                )}
              </div>
              <Input
                ref={fileRef}
                type="file"
                accept="application/pdf,image/*"
                className="hidden"
                onChange={(e) => onAttach(e.target.files?.[0])}
                data-testid="ap-add-doc-input"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={uploading}
                onClick={() => fileRef.current?.click()}
                data-testid="ap-add-doc-attach"
                className="flex-shrink-0"
              >
                {uploading ? "Uploading…" : form.document_url ? "Replace" : "Attach"}
              </Button>
            </div>
            {docError && (
              <div className="mt-2 rounded-md bg-red-50 px-2 py-1 text-[11px] text-red-700" data-testid="ap-add-doc-error">{docError}</div>
            )}
          </div>
          <div className="col-span-2">
            <Label>Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
        </div>
        {dupWarn && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3" data-testid="ap-dup-warning">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-800">
              <WarningCircle size={14} weight="fill" /> Possible duplicate bill
            </div>
            <div className="mt-1 text-xs text-amber-900">{dupWarn.message}</div>
            <ul className="mt-1.5 space-y-0.5">
              {(dupWarn.matches || []).map((m) => (
                <li key={m.id} className="text-[11px] font-mono-alt text-amber-800">
                  {m.bill_number} · {m.vendor_name} · {formatCurrency(m.total)} · {formatDate(m.bill_date)} · {m.status}
                </li>
              ))}
            </ul>
            <div className="mt-2 flex gap-2">
              <Button size="sm" variant="outline" className="h-7 border-amber-400 text-amber-800 hover:bg-amber-100"
                onClick={() => submit(true)} disabled={busy} data-testid="ap-dup-force">
                Create anyway (override is audit-logged)
              </Button>
              <Button size="sm" variant="ghost" className="h-7" onClick={() => setDupWarn(null)} data-testid="ap-dup-dismiss">
                Dismiss
              </Button>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => submit(false)} disabled={busy} className="btn-primary-blue" data-testid="ap-add-confirm">
            {busy ? "Saving…" : "Create bill"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ──────────────────────────────────────────────────────────────
// Vendor autocomplete — combobox showing existing vendors with their
// AP outstanding balance. Falls back to free-text for brand-new vendors.
function VendorAutocomplete({ value, vendorId, suggestions, onSelect, onFreeText }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const needle = (q || value || "").trim().toLowerCase();
    if (!needle) return suggestions.slice(0, 30);
    return suggestions
      .filter((v) => v.name.toLowerCase().includes(needle) || (v.vendor_no || "").toLowerCase().includes(needle))
      .slice(0, 30);
  }, [q, value, suggestions]);
  const exactMatch = suggestions.find((v) => v.name.trim().toLowerCase() === (value || "").trim().toLowerCase());
  const showFreeText = (q || value || "").trim() && !exactMatch;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex h-10 w-full items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-left hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition-colors"
          data-testid="ap-add-vendor"
        >
          <span className={`truncate ${value ? "text-slate-900" : "text-slate-400"}`}>
            {value || "Search or type a new vendor…"}
          </span>
          <div className="ml-2 flex items-center gap-1 flex-shrink-0">
            {vendorId && (
              <span className="rounded-full bg-emerald-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-emerald-700" data-testid="ap-add-vendor-linked">
                LINKED
              </span>
            )}
            <CaretDown size={12} weight="bold" className="text-slate-400" />
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start" data-testid="ap-add-vendor-popover">
        <Command shouldFilter={false}>
          <div className="flex items-center border-b border-slate-100 px-3">
            <MagnifyingGlass size={12} className="mr-2 text-slate-400" />
            <CommandInput
              placeholder="Search vendors or type a new name…"
              value={q}
              onValueChange={(v) => { setQ(v); onFreeText(v); }}
              data-testid="ap-add-vendor-search"
              className="h-9 flex-1 border-0 focus:ring-0"
            />
          </div>
          <CommandList className="max-h-64">
            <CommandEmpty>
              <div className="py-3 px-3 text-xs text-slate-500">
                No matching vendors.
                {showFreeText && (
                  <div className="mt-1 text-slate-700">
                    Press <span className="rounded bg-slate-100 px-1 font-mono-alt">Enter</span> or close this menu to add <b>“{q || value}”</b> as a new one-off vendor.
                  </div>
                )}
              </div>
            </CommandEmpty>
            {filtered.length > 0 && (
              <CommandGroup heading={`${filtered.length} vendor${filtered.length === 1 ? "" : "s"}`}>
                {filtered.map((v) => (
                  <CommandItem
                    key={`${v.id || v.name}-${v.from}`}
                    value={v.name}
                    onSelect={() => { onSelect(v); setQ(""); setOpen(false); }}
                    className="flex items-center justify-between gap-2 cursor-pointer"
                    data-testid={`ap-add-vendor-option-${(v.name || "").replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 text-sm">
                        <span className="truncate font-medium text-slate-900">{v.name}</span>
                        {v.preferred && (
                          <span className="rounded-full bg-blue-50 px-1.5 py-0 text-[9px] font-semibold uppercase text-blue-700">★ Preferred</span>
                        )}
                        {v.from === "ap_only" && (
                          <span className="rounded-full bg-amber-50 px-1.5 py-0 text-[9px] font-semibold uppercase text-amber-700">One-off</span>
                        )}
                      </div>
                      <div className="text-[10px] text-slate-500 font-mono-alt">
                        {v.vendor_no || "—"}{v.category && v.from !== "ap_only" ? ` · ${v.category.replace(/_/g, " ")}` : ""}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className={`font-mono-alt tabular-nums text-xs font-semibold ${v.outstanding > 0 ? "text-red-700" : "text-slate-400"}`}>
                        {v.outstanding > 0 ? formatCurrency(v.outstanding) : "—"}
                      </div>
                      <div className="text-[9px] uppercase tracking-wider text-slate-400">
                        {v.bill_count > 0 ? `${v.bill_count} bill${v.bill_count === 1 ? "" : "s"} outstanding` : "no open bills"}
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
